/**
 * ScanRow — per-row UI for ScanHistoryScreen (FE #124.3).
 *
 * Renders one scan: thumbnail + room label + status pill + count +
 * relative time + status-specific CTA. Tap navigates per-status:
 *   IN_PROGRESS    → Resume in SegmentScreen
 *   READY_TO_SAVE  → SegmentScreen ready for Save
 *   FAILED         → SegmentScreen with [Retry] surfaced
 *   COMPLETED      → SceneViewer (read-only view of saved items)
 *   CANCELLED      → no nav (row hidden in default list anyway)
 *
 * Long-press → discard confirm dialog (handled by parent screen).
 *
 * Wireframe spec lives in USJ.md J2.B4 v2.
 */

import React, { useMemo } from 'react';
import {
  ActivityIndicator,
  Image,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import type { ScanHistoryItem } from '../../../api/types';

interface ScanRowProps {
  scan: ScanHistoryItem;
  onPress: (scan: ScanHistoryItem) => void;
  onLongPress?: (scan: ScanHistoryItem) => void;
  /** Visible discard/delete affordance. When set, a trailing trash
   *  button renders (a real tap/click target — web + phone-web — NOT a
   *  long-press gesture). Fires the same handler as `onLongPress`. */
  onDiscard?: (scan: ScanHistoryItem) => void;
  /** Set when an in-flight delete is happening for this row.
   *  Disables the row + shows a spinner. */
  discarding?: boolean;
}

/* ──────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────── */

/** Same helper used by SegmentScreen header chip (#121). Title-case
 *  the BE's snake_case enum slugs ("DINING_ROOM" → "Dining room"). */
function formatRoomLabel(slug: string | null | undefined): string {
  if (!slug) return 'Untitled scan';
  const cleaned = slug.replace(/_/g, ' ').toLowerCase().trim();
  if (cleaned.length === 0) return 'Untitled scan';
  return cleaned[0].toUpperCase() + cleaned.slice(1);
}

/** Relative time formatter matching the J1 fix (#111 — formatRelativeShort
 *  on the group inventory row). Buckets:
 *   <1m  → "just now"
 *   <1h  → "47s ago" / "12 min ago"
 *   <24h → "5h ago"
 *   <7d  → "3d ago"
 *   else → date string. */
function formatRelativeTime(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const ms = Date.now() - ts;
  if (ms < 60_000) {
    const sec = Math.max(1, Math.floor(ms / 1000));
    return `${sec}s ago`;
  }
  if (ms < 60 * 60_000) {
    return `${Math.floor(ms / 60_000)} min ago`;
  }
  if (ms < 24 * 60 * 60_000) {
    return `${Math.floor(ms / (60 * 60_000))}h ago`;
  }
  if (ms < 7 * 24 * 60 * 60_000) {
    return `${Math.floor(ms / (24 * 60 * 60_000))}d ago`;
  }
  return new Date(ts).toLocaleDateString();
}

/** Status pill metadata — color tone + label + icon. Symbol legend
 *  matches USJ.md J2.B4 v2. */
function statusVisuals(status: ScanHistoryItem['status']): {
  bg: string;
  fg: string;
  label: string;
  icon: keyof typeof MaterialIcons.glyphMap;
} {
  switch (status) {
    case 'IN_PROGRESS':
      return {
        bg: fpColors.primaryContainer,
        fg: fpColors.primary,
        label: 'Scanning…',
        icon: 'sync',
      };
    case 'READY_TO_SAVE':
      return {
        bg: (fpColors as any).tertiaryContainer ?? '#FFE7C2',
        fg: (fpColors as any).onTertiaryContainer ?? '#7A4500',
        label: 'Ready to save',
        icon: 'pending',
      };
    case 'COMPLETED':
      return {
        bg: fpColors.surfaceContainerHighest,
        fg: fpColors.onSurface,
        label: 'Saved',
        icon: 'check-circle',
      };
    case 'FAILED':
      return {
        bg: (fpColors as any).errorContainer ?? '#FFDAD6',
        fg: (fpColors as any).onErrorContainer ?? '#B3261E',
        label: 'Failed',
        icon: 'error-outline',
      };
    case 'CANCELLED':
      return {
        bg: fpColors.surfaceContainerLow,
        fg: fpColors.onSurfaceVariant,
        label: 'Discarded',
        icon: 'block',
      };
  }
  // Defensive default — if BE ever returns a new status value we don't
  // know yet (or a stale poll lands with `undefined`), render a generic
  // "Unknown" pill instead of throwing. The crash this guards against:
  //   `TypeError: undefined is not an object (evaluating 'a.label')`
  //   when a poll-refreshed row landed with status: undefined and
  //   ScanRow tried to render `v.label`.
  return {
    bg: fpColors.surfaceContainerLow,
    fg: fpColors.onSurfaceVariant,
    label: 'Unknown',
    icon: 'help-outline',
  };
}

/** Per-status secondary CTA text. The whole row is tappable; this is
 *  the visual affordance on the right. */
function ctaText(status: ScanHistoryItem['status']): string {
  switch (status) {
    case 'IN_PROGRESS':
      return 'View';
    case 'READY_TO_SAVE':
      return 'Save →';
    case 'FAILED':
      return 'Retry';
    case 'COMPLETED':
      return 'Open';
    case 'CANCELLED':
      return '';
  }
  // Defensive default — see the matching guard in `statusVisuals`.
  return '';
}

/* ──────────────────────────────────────────────────────────────────
 * Component
 * ────────────────────────────────────────────────────────────────── */

export const ScanRow: React.FC<ScanRowProps> = ({
  scan,
  onPress,
  onLongPress,
  onDiscard,
  discarding = false,
}) => {
  const v = useMemo(() => statusVisuals(scan.status), [scan.status]);
  const cta = useMemo(() => ctaText(scan.status), [scan.status]);
  const title = formatRoomLabel(scan.room_context);

  // Subtitle: "12 items detected" / "11 items saved" / "Pipeline error"
  const subtitle = useMemo(() => {
    if (scan.status === 'FAILED' && scan.error_code) {
      return errorCodeLabel(scan.error_code);
    }
    if (scan.status === 'COMPLETED') {
      return `${scan.saved_count} item${scan.saved_count === 1 ? '' : 's'} saved`;
    }
    if (scan.status === 'IN_PROGRESS') {
      return `${scan.detected_count} item${scan.detected_count === 1 ? '' : 's'} detected so far`;
    }
    return `${scan.detected_count} item${scan.detected_count === 1 ? '' : 's'} detected`;
  }, [scan]);

  const timeStr = formatRelativeTime(scan.last_event_at);

  // COMPLETED rows delete the scan *record* (saved items stay); the
  // rest discard an unsaved scan. Drives the trash button's a11y label.
  const discardLabel =
    scan.status === 'COMPLETED' ? 'Delete scan record' : 'Discard scan';

  return (
    <View style={styles.row}>
      <Pressable
        onPress={() => onPress(scan)}
        onLongPress={() => onLongPress?.(scan)}
        disabled={discarding}
        accessibilityRole="button"
        accessibilityLabel={`${title} scan, ${v.label}, ${subtitle}`}
        testID={`scan-row-${scan.file_record_id}`}
        style={({ pressed }) => [
          styles.rowMain,
          pressed && !discarding ? { opacity: 0.7 } : null,
          discarding && { opacity: 0.4 },
        ]}
      >
        {/* Thumbnail (left) */}
        <View style={styles.thumb}>
        {scan.thumbnail_url ? (
          <Image
            source={{ uri: scan.thumbnail_url }}
            style={styles.thumbImg}
            accessibilityLabel=""
          />
        ) : (
          <MaterialIcons
            name="image"
            size={28}
            color={fpColors.onSurfaceVariant}
          />
        )}
      </View>

      {/* Center column — title + subtitle + status pill + time */}
      <View style={styles.body}>
        <View style={styles.titleRow}>
          <Text style={styles.title} numberOfLines={1}>
            {title}
          </Text>
          {/* Status pill */}
          <View style={[styles.pill, { backgroundColor: v.bg }]}>
            <MaterialIcons name={v.icon} size={11} color={v.fg} />
            <Text style={[styles.pillText, { color: v.fg }]}>{v.label}</Text>
          </View>
        </View>

        <Text style={styles.subtitle} numberOfLines={1}>
          {subtitle}
        </Text>

        {/* Progress bar — only on IN_PROGRESS. Indeterminate-style:
            we don't know the total upfront, so animate a slim accent
            band across. */}
        {scan.status === 'IN_PROGRESS' ? (
          <View style={styles.progressTrack}>
            <View style={styles.progressFill} />
          </View>
        ) : null}

        <Text style={styles.timeStr}>{timeStr}</Text>
      </View>

        {/* Right CTA — status label only; discard moved to the trash btn. */}
        <View style={styles.ctaWrap}>
          {cta ? <Text style={styles.ctaText}>{cta}</Text> : null}
        </View>
      </Pressable>

      {/* Trailing discard / delete — a real tap/click target (web +
          phone-web), NOT a long-press gesture. Sibling of the navigate
          Pressable so its tap can't bubble into row-navigate. */}
      {onDiscard ? (
        <Pressable
          onPress={() => onDiscard(scan)}
          disabled={discarding}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={discardLabel}
          testID={`scan-discard-${scan.file_record_id}`}
          style={({ pressed }) => [
            styles.discardBtn,
            pressed && !discarding ? { opacity: 0.6 } : null,
          ]}
        >
          {discarding ? (
            <ActivityIndicator size="small" color={fpColors.error} />
          ) : (
            <MaterialIcons
              name="delete-outline"
              size={20}
              color={fpColors.error}
            />
          )}
        </Pressable>
      ) : null}
    </View>
  );
};

/** Map BE error_code strings to a user-friendly subtitle. Mirror
 *  backend#313's error_code enum. */
function errorCodeLabel(code: string): string {
  switch (code) {
    case 'pipeline_5xx':
      return 'AI pipeline error — tap to retry';
    case 'sse_drop':
      return 'Connection dropped — tap to retry';
    case 'ai_quota_exceeded':
      return 'AI rate-limited — try again in a few min';
    default:
      return 'Scan failed — tap to retry';
  }
}

/* ──────────────────────────────────────────────────────────────────
 * Styles
 * ────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  // Outer container — the navigate Pressable + the trailing discard
  // button sit side-by-side inside this. Owns the surface + divider.
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: fpColors.surface,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    gap: 8,
  },
  // Inner navigate target — thumb + body + CTA label.
  rowMain: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  // Trailing trash button. ~40px wide tap target for mouse + touch.
  discardBtn: {
    width: 40,
    alignSelf: 'stretch',
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumb: {
    width: 64,
    height: 64,
    borderRadius: 8,
    backgroundColor: fpColors.surfaceContainerLow,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbImg: { width: '100%', height: '100%' },
  body: { flex: 1 },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  title: {
    flexShrink: 1,
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  pillText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 10,
    letterSpacing: 0.2,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    marginBottom: 4,
  },
  // Indeterminate progress band — see USJ.md B4 wireframe.
  // The fill width is 40% sliding via a CSS animation on web; on
  // native we fall back to the static highlight (no animation infra).
  progressTrack: {
    height: 3,
    borderRadius: 1.5,
    backgroundColor: fpColors.surfaceContainerHighest,
    marginVertical: 4,
    overflow: 'hidden',
  },
  progressFill: {
    width: '40%',
    height: '100%',
    backgroundColor: fpColors.primary,
    borderRadius: 1.5,
  },
  timeStr: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.outline,
    marginTop: 2,
  },
  ctaWrap: {
    minWidth: 56,
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingTop: 4,
  },
  ctaText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.primary,
  },
});
